const _globalWidgetCfgProp = 'belgingur_widgetcfg';
const _dataAttrPrefix = 'data-belgingur-';  // prefix used for all embedding-instruction attributes.


/*
 _findRefElm
 ===========================================================================
 Attempts to find a single(!) reference (injection/replacement) Element
 for a given config object.

 The `replace` setting trumps `appendto`.
 If neither is specified it defaults to looking for a HTML element inside
 <body> with a matching `data-belgingur-deployment=""` attribute
 and interpret it as a "replacement" point.

 Reference points can be one of:
 * a CSS selector (String)
 * Element
 * an Element contained in an Array-like list

 Returns `null` if no element is found
 */
const _findRefElm = function (cfg) {
    let refElm = cfg.replace || cfg.appendto;
    if (!refElm || refElm === 'this') {
        // Default to scourging for HTML elements with a matching belgingur-deplyment attribute.
        // Generally this shouldn't be happening
        refElm = document.querySelector('body [' + _dataAttrPrefix + 'widget="' + cfg.widget + '"]');
    }
    else if (typeof refElm === 'string') {
        // Treat refElm as a CSS selector
        refElm = document.querySelector(refElm);
    }
    else if (!(refElm instanceof Element)) {
        // Assume refElm is an Array-like object containign an element
        refElm = refElm[0] || null;
    }
    return refElm;
};


/*
 findConfigs
 ===========================================================================
 Returns an Array of found config objects.
 Each config object is only returned once.
 Returns an empty Array if no matches are found.

 Config objects are read from `window.belgingur_widgetcfg`.
 The function also looks for HTML elements with `data-belgingur-depyment=""`
 attributes and extracts config info from those (once only for each)
 and stores it in `window.belgingur_widgetcfg`.

 Found configs are removed
 */
const findConfigs = function (idPrefix) {
    const belgingur_widgetcfg = window[_globalWidgetCfgProp] || (window[_globalWidgetCfgProp] = []);

    // find all HTML elements with with embedding instructions, which haven't been
    // flagged as having their instruction-values "parsed"/parsed (via a data-attribute).
    const embeddingElms = document.querySelectorAll('[' + _dataAttrPrefix + 'widget]:not([' + _dataAttrPrefix + 'parsed])');
    const _dataAttrPrefixLength = _dataAttrPrefix.length;
    [].slice.call(embeddingElms).forEach(function (elm) {
        const cfg = {};
        [].slice.call(elm.attributes).forEach(function (attr) {
            if (attr.name.substr(0, _dataAttrPrefixLength) === _dataAttrPrefix) {
                cfg[attr.name.substr(_dataAttrPrefixLength)] = attr.value;
            }
        });
        belgingur_widgetcfg.push(cfg);
        if (typeof cfg.inline === 'string') {
            // Treat all string-values for `inline` as false – except for literal "true"
            cfg.inline = ( cfg.inline.toLowerCase() === 'true' );
        }
        // Flag the fact that we've now parsed this HTML element
        // and stored its instructions in `window.belgingur_widgetcfg`
        elm.setAttribute(_dataAttrPrefix + 'parsed', '');
    });

    // Filter matched configs from `belgingur_widgetcfg` that have
    // a `widget` property that starts with idPrefix.
    const unmatchedConfigs = [];
    const matchedConfigs = belgingur_widgetcfg.filter(function (cfg) {
        const matches = cfg.widget.indexOf(idPrefix) === 0;
        if (!matches) {
            unmatchedConfigs.push(cfg);
        }
        return matches;
    });

    // Empty `belgingur_widgetcfg` and push unmatched configs back into it
    belgingur_widgetcfg.length = 0;
    if (unmatchedConfigs.length) {
        belgingur_widgetcfg.push.apply(belgingur_widgetcfg, unmatchedConfigs);
    }
    else {
        delete window[_globalWidgetCfgProp];
    }

    return matchedConfigs;
};


/*
 prepWrapper
 ===========================================================================
 Returns a new <div> or <iframe> Element depending on the config
 If a reference (injection/replacement) Element is found (based on
 the config info), then the <iframe>/<div> automatically replaces it
 (or is inserted into it) as indicated by the config properties.
 Otherwise the caller is assumed to handle the DOM-insertion later on.
 */
const prepWrapper = function (cfg) {
    let wrapperElm;
    if (cfg.inline) {
        wrapperElm = document.createElement('div');
    }
    else {
        // Create an iframe which will have a _wait_for_load flag until it is loaded
        wrapperElm = document.createElement('iframe');
        wrapperElm._wait_for_load = true;
        wrapperElm.onload = () => delete wrapperElm._wait_for_load;

        wrapperElm.src = 'about:blank';
        wrapperElm.frameBorder = 0;
        wrapperElm.scrolling = 'no';
        const wrapperElmStyle = wrapperElm.style;
        wrapperElmStyle.height = 0;
        wrapperElmStyle.overflow = 'hidden';
        // The following crap styles are required for iOS browser to contain set the iframe's width
        // Webmasters who want the iframe to be something other than 100% wide - must override
        // this by using `min-width: XXX !important;` declaration in their CSS.
        wrapperElmStyle.width = '200px';
        wrapperElmStyle.minWidth = '100%';
    }
    wrapperElm.setAttribute(_dataAttrPrefix + 'widgetwrapper', cfg.widget);
    // Attempt to find a reference element and inject wrapperElm
    // according to the appendto/replace directives
    const refElm = _findRefElm(cfg);
    if (refElm) {
        if (cfg.appendto && !cfg.replace) { // replace trumps appendto
            refElm.appendChild(wrapperElm);
        }
        else if (refElm.parentNode) {
            refElm.parentNode.replaceChild(wrapperElm, refElm);
        }
    }

    return wrapperElm;
};


const getGlobalBelgingurObject = function () {
    const belgingur = window.belgingur || (window.belgingur = {});
    belgingur.widgets = belgingur.widgets || {};
    belgingur.states = belgingur.states || {};
    belgingur.$injectorify = belgingur.$injectorify || function (widgetFactory, idPrefix) {
        return function () {

            // Locate all configs for this widget and their wrapper elements and ensure each is initialised
            findConfigs(idPrefix).forEach(function (cfg) {
                const wrapperElm = prepWrapper(cfg);
                if (!wrapperElm.parentNode) return;

                // Decide whether to initialise widget now or when the wrapper has loaded
                if (wrapperElm._wait_for_load) {
                    //console.log('Wait for wrapper to load before initialising widget');
                    delete wrapperElm._wait_for_load;
                    wrapperElm.onload = initWidget;
                } else {
                    //console.log('Initialise widget immediately');
                    initWidget();
                }

                // Initialise a configuration in a wrapper element assuming the element is ready
                function initWidget() {
                    const widget = widgetFactory(cfg, wrapperElm);
                    console.log("Construct widget " + widget.widgetId);
                    widget.start();
                    belgingur.states[widget.widgetId] = widget.state;
                    if (Object.keys(belgingur.states).length === 1) {
                        belgingur.state = widget.state;
                    } else {
                        delete belgingur.state;
                    }
                }

            });
        };
    };
    return belgingur;
};


/*
 injector
 ===========================================================================
 General purpose widget embedding function for Belgingur.
 Accepts a widgetFactory reference and idPrefix token.
 Automatically scans the DOM for embedding instructions for widgets with
 this idPrefix and instantly injects and starts-up a widget for each one.
 This function is safe to run multiple times and creates only one instance
 for each set of embedding instructions found.
 Returns nothing.
 */
const injector = function (widgetFactory, idPrefix) {
    const belgingur = getGlobalBelgingurObject();
    const widgetName = widgetFactory.widgetName;
    const widgets = belgingur.widgets;
    const widgetInjector = widgets[widgetName] || (widgets[widgetName] = {
            factory: widgetFactory,
            inject: belgingur.$injectorify(widgetFactory, idPrefix),
        });
    if (document.readyState !== 'loading') {
        widgetInjector.inject();
    } else {
        document.addEventListener('DOMContentLoaded', widgetInjector.inject);
    }
};


export default injector;
